/**
 * Skeleton.js — lightweight shimmering placeholders.
 * No external deps; uses inline CSS keyframes defined in App.css (`@keyframes pulse`).
 *
 *   <Skeleton width={200} height={14} />                 // single bar
 *   <SkeletonText lines={3} />                           // multi-line text
 *   <SkeletonTable rows={5} cols={6} />                  // table placeholder
 *   <SkeletonCard />                                     // KPI tile placeholder
 *
 * Why skeletons not spinners (per Rudrik loading rules):
 *   - Never block users with infinite spinners
 *   - Always show structural shape so they know data is coming
 */
import React from "react";

const baseStyle = {
  display: "inline-block",
  background: "linear-gradient(90deg, #1a2433 0%, #232f42 50%, #1a2433 100%)",
  backgroundSize: "200% 100%",
  animation: "skeleton-shimmer 1.4s ease-in-out infinite",
  borderRadius: 4,
};

// Inject shimmer keyframes once
let _injected = false;
function injectKeyframes() {
  if (_injected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.innerHTML = `
    @keyframes skeleton-shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;
  document.head.appendChild(el);
  _injected = true;
}

export function Skeleton({ width = "100%", height = 14, style = {}, ...rest }) {
  injectKeyframes();
  return <span style={{ ...baseStyle, width, height, ...style }} {...rest} />;
}

export function SkeletonText({ lines = 3, lineHeight = 12, gap = 8 }) {
  injectKeyframes();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height={lineHeight} width={i === lines - 1 ? "60%" : "100%"} />
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 6, headerHeight = 28, rowHeight = 18 }) {
  injectKeyframes();
  return (
    <div data-testid="skeleton-table" style={{ width: "100%" }}>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, padding: "10px 14px", borderBottom: "1px solid #1e2530" }}>
        {Array.from({ length: cols }, (_, i) => <Skeleton key={i} height={headerHeight} />)}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, padding: "10px 14px", borderBottom: "1px solid #0f1520" }}>
          {Array.from({ length: cols }, (_, c) => <Skeleton key={c} height={rowHeight} width={c === 0 ? "85%" : "70%"} />)}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCard({ height = 90 }) {
  injectKeyframes();
  return (
    <div className="card-aml" style={{ padding: 16 }}>
      <Skeleton width={36} height={36} style={{ borderRadius: 8, marginBottom: 12 }} />
      <Skeleton width="60%" height={11} style={{ marginBottom: 6 }} />
      <Skeleton width="40%" height={20} />
      <div style={{ height: height - 80 }} />
    </div>
  );
}

export default Skeleton;
